// apps/web/src/pages/PreApproval/PreApprovalBanner.tsx

import { useState } from 'react';

import { ArrowRight, GraduationCap, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '@alamo/auth';
import { Button, cn } from '@alamo/ui';

const SESSION_KEY = 'pre_approval_banner_dismissed';

/**
 * Amber warning banner shown at the top of the page for pre-approved consultants
 * (status=approved but not yet active). Informs them that some features are restricted.
 */
export function PreApprovalBanner() {
  const { t } = useTranslation('pre_approval');
  const { onboardingCompletion } = useAuth();
  const onboardingBlocked = onboardingCompletion?.is_blocked === true;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === 'true';
    } catch {
      return false;
    }
  });

  if (dismissed && !onboardingBlocked) return null;

  return (
    <div
      role="alert"
      className={cn('flex items-center gap-3 px-4 py-3 bg-amber-50 border-b border-amber-200')}
    >
      <Info className="h-5 w-5 text-amber-600 shrink-0" aria-hidden="true" />
      <p className="flex-1 text-sm font-body text-amber-800">
        {t(onboardingBlocked ? 'banner.onboarding_text' : 'banner.text')}
      </p>
      <Link
        to={onboardingBlocked ? '/training' : '/my-registration'}
        className="inline-flex items-center gap-1 text-sm font-body font-medium text-amber-900 hover:text-amber-700 transition-colors shrink-0"
      >
        {onboardingBlocked && <GraduationCap className="h-4 w-4" aria-hidden="true" />}
        {t(onboardingBlocked ? 'banner.start_training' : 'banner.view_details')}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
      {!onboardingBlocked && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setDismissed(true);
            try {
              sessionStorage.setItem(SESSION_KEY, 'true');
            } catch {
              // sessionStorage may be unavailable
            }
          }}
          className="text-amber-700 hover:text-amber-900 hover:bg-amber-100 shrink-0"
          aria-label={t('banner.dismiss')}
        >
          {t('banner.dismiss')}
        </Button>
      )}
    </div>
  );
}
